import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AuthService } from 'src/app/services/auth.service';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ScopeService } from 'src/app/services/scope.service';

import { UserStateService } from 'src/app/services/user-state.service';
import { Status } from 'src/app/types/status';

/**
 * application toolbar
 *
 * holding logo, and - depending on login state
 * - login button
 * - avatar & username, with menu with further options
 */
@Component( {
    selector: 'app-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    standalone: false
} )
export class ToolbarComponent implements OnInit {
    #breakpointService = inject( BreakpointService );
    isMobile = computed( () => this.#breakpointService.currentScreenSize() === 'XSmall' );

    /** environment flags */
    isProduction = environment.name === 'production';

    showCommunityMap = environment.optionalModules.showCommunityMap;
    showScopeCommunity = environment.optionalModules.showScopeCommunity;
    showUnLocodes = environment.optionalModules.showUnLocodes;
    showUserAdmin = environment.optionalModules.showUserAdmin;
    showStyleGuide = environment.optionalModules.showStyleGuide;
    showOrganization = environment.optionalModules.showOrganization;
    switchTenants = environment.optionalModules.switchTenants;

    /**
     * tile & badge, showing current environment
     * only shown when not in production mode
     */
    title = 'Scope';
    checkEnvironment = environment.name;

    /**
     * text of enviroment links
     * shown in user menu
     */
    testEnv = $localize`:@@toolbar-menu-test-link:Start test environment`;
    liveEnv = $localize`:@@toolbar-menu-live-link:Start live environment`;

    /**
     * links to other environments: DEV <=> PROD
     * shown in user menu
     */
    envText = this.isProduction ? this.testEnv : this.liveEnv;
    envLink = this.isProduction ? 'https://scope.riege.cloud/' : 'https://scope.riege.com/';

    /** default language of link */
    #lang = 'en';

    indicator = signal( '' );
    color = signal( '' );
    status = signal( '' );

    /** link for knowledge base */
    linkKnowledgeBase = '';

    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();

    authService = inject( AuthService );
    #cookieService = inject( CookieService );
    #scopeService = inject( ScopeService );
    #userState = inject( UserStateService );

    readonly currentUser = this.#userState.state.asReadonly();

    organization = computed(
        () => this.currentUser().organization!.displayName
    );

    /**
     * on init subscribe on loggin state
     */
    ngOnInit(): void {
        this.setKnownledgeBaseLinks();
    }

    /**
     * log in helper function
     * calls login in {@link AuthService}
     */
    login() {
        this.authService.login();
    }

    /**
     * log out helper function
     * calls logout in {@link AuthService}
     */
    logout() {
        this.authService.logout();
    }

    // show links to knowledege base depending on language //
    setKnownledgeBaseLinks() {
        const cookieExists: boolean = this.#cookieService.check( 'language' );

        if ( cookieExists ) {
            this.#lang = this.#cookieService.get( 'language' );
        } else {
            this.#lang = navigator.language;
        }

        switch ( this.#lang ) {
            case 'de':
            case 'de-DE':
            case 'de-AT':
            case 'de-CH':
                this.linkKnowledgeBase = 'https://service.riege.com/de/knowledge';
                break;
            case 'es':
                this.linkKnowledgeBase = 'https://service.riege.com/es-mx/knowledge';
                break;
            default:
                this.linkKnowledgeBase = 'https://service.riege.com/en/knowledge';
        }
    }

    /**
     * get status from scope api
     * triggered when user opens help menu
     * and set status icon & color
     */
    getStatus() {
        this.#scopeService
            .getStatus()
            .pipe( takeUntil( this.#destroyed ) )
            .subscribe( ( res: Status ) => {
                const state = res.status.indicator;
                let icon = 'check_circle';
                let color = 'green';
                let status = $localize`:@@toolbar-status-normal:All systems operational`;

                switch ( state ) {
                    case 'minor':
                        icon = 'remove_circle_outline';
                        color = 'yellow';
                        status = $localize`:@@toolbar-status-minor:Degraded performance`;
                        break;
                    case 'partial':
                        icon = 'error_outline';
                        color = 'orange';
                        status = $localize`:@@toolbar-status-partial:Partial outage`;
                        break;
                    case 'major':
                        icon = 'report_problem';
                        color = 'red';
                        status = $localize`:@@toolbar-status-major:Major outage`;
                        break;
                    case 'maintenance':
                        icon = 'build_circle';
                        color = 'blue';
                        status = $localize`:@@toolbar-status-maintenance:Under maintenance`;
                        break;
                }

                this.indicator.set( icon );
                this.color.set( color );
                this.status.set( status );
            } );
    }

    /**
     * helper function to switch Scope tenant
     * @param scopeTID id of scope tenant
     */
    switchTenant( scopeTID: string ) {
        console.log( 'yet not implemented to switch', scopeTID );
        // this.authService.switchTenant(scopeTID);
    }

    ngOnDestroy() {
        this.#destroyed.next();
        this.#destroyed.complete();
    }
}
